<template>
  <v-dialog v-model="dialog" persistent max-width="600">
    <v-card>
      <v-card-title>
        <v-icon left color="primary">mdi-file-document-outline</v-icon>
        {{ isEdit ? "Edit Template" : "New Template" }}
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="form.name"
          label="Template Name *"
          outlined
          dense
        ></v-text-field>
        <v-textarea
          v-model="form.description"
          label="Description"
          outlined
          dense
          rows="2"
        ></v-textarea>
        <v-row dense>
          <v-col cols="6">
            <v-combobox
              v-model="form.category"
              :items="categoryOptions"
              label="Category"
              outlined
              dense
            ></v-combobox>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="form.default_priority"
              :items="priorityOptions"
              label="Default Priority"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-select
              v-model="form.default_recurrence_type"
              :items="recurrenceOptions"
              label="Default Recurrence"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.default_time"
              label="Default Time"
              type="time"
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Weekly day picker -->
        <div v-if="form.default_recurrence_type === 'weekly'" class="mb-3">
          <div class="subtitle-2 mb-1">Default Days of Week:</div>
          <v-chip-group
            v-model="selectedWeekDays"
            multiple
            active-class="primary--text"
          >
            <v-chip v-for="day in weekDays" :key="day.value" filter outlined>
              {{ day.text }}
            </v-chip>
          </v-chip-group>
        </div>

        <!-- Monthly day picker -->
        <v-select
          v-if="form.default_recurrence_type === 'monthly'"
          v-model="selectedMonthDays"
          :items="monthDays"
          label="Default Days of Month"
          multiple
          outlined
          dense
          chips
          small-chips
        ></v-select>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="loading" @click="submit">
          {{ isEdit ? "Update" : "Create" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
export default {
  props: {
    value: { type: Boolean, default: false },
    editTemplate: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      selectedWeekDays: [],
      selectedMonthDays: [],
      form: this.getEmptyForm(),
      priorityOptions: [
        { text: "Basic", value: "basic" },
        { text: "Urgent", value: "urgent" },
        { text: "Very Urgent", value: "very_urgent" },
      ],
      recurrenceOptions: [
        { text: "None (One-Time)", value: "none" },
        { text: "Daily", value: "daily" },
        { text: "Weekly", value: "weekly" },
        { text: "Monthly", value: "monthly" },
      ],
      categoryOptions: [
        "Accounting",
        "Inventory",
        "Maintenance",
        "Administration",
        "Sales",
        "Service",
      ],
      weekDays: [
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
        { text: "Sun", value: 7 },
      ],
      monthDays: Array.from({ length: 31 }, (_, i) => i + 1),
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isEdit() {
      return !!this.editTemplate;
    },
  },
  watch: {
    value(val) {
      if (val) {
        if (this.editTemplate) {
          this.loadEditData();
        } else {
          this.form = this.getEmptyForm();
          this.selectedWeekDays = [];
          this.selectedMonthDays = [];
        }
      }
    },
  },
  methods: {
    ...mapActions({
      create_template: "task_template/create_template",
      update_template: "task_template/update_template",
    }),
    getEmptyForm() {
      return {
        name: "",
        description: "",
        category: "",
        default_priority: "basic",
        default_recurrence_type: "none",
        default_recurrence_days: null,
        default_time: null,
      };
    },
    loadEditData() {
      const t = this.editTemplate;
      this.form = {
        id: t.id,
        name: t.name,
        description: t.description,
        category: t.category,
        default_priority: t.default_priority,
        default_recurrence_type: t.default_recurrence_type,
        default_recurrence_days: t.default_recurrence_days,
        default_time: t.default_time,
        is_active: t.is_active,
      };
      if (
        t.default_recurrence_type === "weekly" &&
        Array.isArray(t.default_recurrence_days)
      ) {
        this.selectedWeekDays = t.default_recurrence_days.map((d) =>
          this.weekDays.findIndex((wd) => wd.value === d)
        );
      }
      if (
        t.default_recurrence_type === "monthly" &&
        Array.isArray(t.default_recurrence_days)
      ) {
        this.selectedMonthDays = t.default_recurrence_days;
      }
    },
    close() {
      this.dialog = false;
    },
    submit() {
      if (!this.form.name) {
        this.$alert({
          title: "Validation",
          message: "Template name is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      const payload = { ...this.form };
      if (payload.default_recurrence_type === "weekly") {
        payload.default_recurrence_days = this.selectedWeekDays.map(
          (idx) => this.weekDays[idx].value
        );
      } else if (payload.default_recurrence_type === "monthly") {
        payload.default_recurrence_days = this.selectedMonthDays;
      } else {
        payload.default_recurrence_days = null;
      }

      this.loading = true;
      const action = this.isEdit ? this.update_template : this.create_template;
      action(payload)
        .then(() => {
          this.loading = false;
          this.$emit("saved");
          this.close();
        })
        .catch((err) => {
          this.loading = false;
          this.$alert({
            title: "Error",
            message: err.response ? err.response.data.error : err.message,
            color: "error",
            icon: "mdi-alert-circle",
          });
        });
    },
  },
};
</script>
